import { type FunctionComponent, type ReactNode } from 'react';
import { type Maybe, type MatchingProductsFieldsFragment } from '../../../__generated__/graphql-client-types';
import { type HeadingValuesType } from '../../../constants/general';
import { getBrandLogoImageRelativeUrl } from '../../../helpers/content-helper/content.helper';
import { Link } from '../../common-components/link/link.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { Model } from '../../product-card/product-line-items/model/model.component';
import { Price } from '../../product-card/product-line-items/price/price.component';
import { ProductLineItems } from '../../product-card/product-line-items/product-line-items.component';
import { ReviewStarRating } from '../../product-card/product-line-items/review-star-rating/review-star-rating.component';
import { Title } from '../../product-card/product-line-items/title/title.component';

export const DefaultElementClasses = 'flex flex-column w-100 w-33-m w-20-l';
export const DefaultElementTileClasses = 'ba b--theme-grey-light br2 popout shadow-hover f5';

export type ProductCardListBrandElementProps = {
	brandName: string;
	collectionName?: string;
	collectionUrl?: string;
	className?: string;
	tileClass?: string;
};

/**
 * Product List Card Brand Element
 *
 * @param {ProductCardListBrandElementProps} props
 */
export const ProductListCardBrandElement: FunctionComponent<ProductCardListBrandElementProps> = ({
	brandName,
	collectionName,
	collectionUrl,
	className = DefaultElementClasses,
	tileClass = DefaultElementTileClasses
}) => {
	return collectionName && collectionUrl ? (
		<li className={`${className}`}>
			<div className={`${tileClass} ma1 h-100`}>
				<Link url={collectionUrl} className="flex flex-column items-center justify-center w-100 h-100 pa3">
					<CloudinaryImage
						publicID={`${getBrandLogoImageRelativeUrl(brandName)}.gif`}
						description={`${brandName} logo`}
						options={{ width: undefined, height: 60, crop: 'lpad' }}
					/>
					<span className="mt2 tc f6 lh-copy theme-black underline-hover">View the entire {collectionName} Collection</span>
				</Link>
			</div>
		</li>
	) : null;
};

export type ProductCardElementProps = {
	className?: string;
	tileClass?: string;
} & MatchingProductsFieldsFragment;

/**
 * Default Product List Card
 *
 * @param {ProductCardElementProps} props
 */
export const ProductDefaultListCardElement: FunctionComponent<ProductCardElementProps> = ({
	title,
	brandName,
	modelNumber,
	url,
	image,
	rating,
	priceInfo,
	squareFootageBased,
	className = DefaultElementClasses,
	tileClass = DefaultElementTileClasses
}) => {
	const { min, max } = priceInfo.range || {};
	const minPrice = min || 0;
	const maxPrice = max || 0;

	const isMinPriceAvailable = Boolean(minPrice && minPrice > 0);
	// MaxPrice depends on the availability of min price in order to display the range
	const isMaxPriceAvailable = isMinPriceAvailable && maxPrice && maxPrice > 0;

	return (
		<li className={`${className}`}>
			<div className={`${tileClass} ma1 h-100`}>
				<Link url={url} className="flex flex-column h-100 pa3 theme-grey-darker">
					<div className="flex flex-column items-start pa1">
						{image && (
							<CloudinaryImage
								publicID={image.id}
								description={image.description}
								options={{ height: 55, width: undefined, crop: 'lpad' }}
							/>
						)}
					</div>
					<ProductLineItems
						fixedTitleModelHeight
						Title={<Title brandName={brandName} title={title} titleTruncation="tc2-title" className="mt2 underline-hover" />}
						Model={<Model modelNumber={modelNumber} truncate />}
						ReviewStarRating={
							rating ? (
								<ReviewStarRating
									reviewCount={rating.reviewCount}
									ratingValue={rating.ratingValue}
									productUrl={url}
									readOnly
								/>
							) : undefined
						}
						Price={
							priceInfo.current ? (
								<Price
									priceRange={{
										min: isMinPriceAvailable ? minPrice : priceInfo.current,
										max: isMaxPriceAvailable ? maxPrice : undefined,
										squareFootageBased,
										className: 'mt1 nowrap normal'
									}}
								/>
							) : undefined
						}
					/>
				</Link>
			</div>
		</li>
	);
};

export type ProductCardListProps = {
	title?: string;
	description?: Maybe<string>;
	className?: string;
	listClass?: string;
	children?: ReactNode;
	headingValue?: HeadingValuesType;
};

/**
 * Product Card List Component
 *
 * @param {ProductCardListProps} props
 */
export const ProductCardList: FunctionComponent<ProductCardListProps> = (props: ProductCardListProps) => {
	const { className, listClass, title, description, children, headingValue = 'h2' } = props;
	const DEFAULT_LIST_CLASSES = listClass ?? 'flex flex-row flex-wrap w-100 ph1 nl2';
	const HeadingTag = headingValue;

	return children ? (
		<section className={`flex flex-column w-100 justify-between ${className ?? ''}`.trim()}>
			{title ? <HeadingTag className="ma0 f4 lh-title">{title}</HeadingTag> : null}
			{description ? <p className="mt1 mh0 mb0 f6 lh-copy">{description}</p> : null}
			<ul className={`list mt2 mh0 mb0 ${DEFAULT_LIST_CLASSES}`}>{children}</ul>
		</section>
	) : null;
};
